/**
 * Schema for `appreciation_nudges/{nudgeId}` — the data-driven catalogue
 * of pass-it-forward suggestions surfaced inside the appreciation compose
 * modal.
 *
 * Each document represents one nudge. The FE filters to enabled nudges
 * whose `appliesTo` matches the current sender/recipient direction (and
 * whose optional `courseId` matches the active course), sorts by
 * `priority`, and renders the top-N as cards under the body textarea.
 *
 * Admin tooling is V1.1.3; for V1.1.2 ([#2952](https://github.com/CodeinPlace/cip/issues/2952))
 * the seed script writes the default SL-recruitment nudge and additional
 * nudges are added directly via the Firebase Console.
 */

import {z} from 'zod';

/**
 * Sender role tag for nudge targeting. Mirrors the persisted
 * `appreciation/{courseId}/attempts/{attemptId}.senderRole` enum so
 * admins set the same value in both places.
 */
export const NudgeSenderRoleSchema = z.enum(['ta', 'sectionLeader', 'student']);
/** Sender role string. */
export type NudgeSenderRole = z.infer<typeof NudgeSenderRoleSchema>;

/**
 * Recipient role tag for nudge targeting. Mirrors
 * `appreciation/{courseId}/attempts/{attemptId}.recipientRole`.
 */
export const NudgeRecipientRoleSchema = z.enum(['sectionLeader', 'student']);
/** Recipient role string. */
export type NudgeRecipientRole = z.infer<typeof NudgeRecipientRoleSchema>;

/**
 * Union of every role tag that may appear on a nudge — either side of
 * the `appliesTo` block. Useful for code that displays the role to a
 * user without caring whether it came from `senderRole` or
 * `recipientRole` (e.g. signing off an appreciation note with
 * "Your Section Leader").
 */
export type NudgeRole = NudgeSenderRole | NudgeRecipientRole;

/**
 * Maps a nudge role tag to the display string used in user-facing
 * surfaces (subject parentheticals, body sign-offs, admin dashboard
 * columns). Kept in `@cip/core` so the seed script and the webapp
 * compose modal stay in lockstep.
 *
 * Note: `'ta'` deliberately renders as `'Head TA'` here. In the
 * appreciation feature the recipient sees the relationship made
 * explicit, and "Head TA" is how Code in Place students and SLs refer
 * to a TA in conversation. The webapp's wider `roleToDisplayString`
 * in `contexts/profileUtil` returns `'TA'` instead, which is correct
 * for the rest of the app; the divergence is intentional and scoped
 * to this surface.
 *
 * @param role - The nudge role tag (`'ta'`, `'sectionLeader'`, or
 *   `'student'`).
 * @returns The display string (`'Head TA'`, `'Section Leader'`, or
 *   `'Student'`).
 */
export function displayNudgeRole(role: NudgeRole): string {
  switch (role) {
    case 'ta':
      return 'Head TA';
    case 'sectionLeader':
      return 'Section Leader';
    case 'student':
      return 'Student';
  }
}

/**
 * `appliesTo` block: a nudge fires only when (senderRole, recipientRole)
 * matches both fields. The valid directions are the ones the send callable
 * grants standing for: `ta → sectionLeader`, `sectionLeader → student`, and
 * `student → sectionLeader` (a student to the SL of their own section).
 */
export const NudgeAppliesToSchema = z.object({
  senderRole: NudgeSenderRoleSchema,
  recipientRole: NudgeRecipientRoleSchema,
});
/** Parsed `appliesTo` block. */
export type NudgeAppliesTo = z.infer<typeof NudgeAppliesToSchema>;

/**
 * Persisted shape of an `appreciation_nudges` document.
 *
 * **Interpolation tokens supported in `title` and `text`:**
 * - `{recipientFirstName}` — substituted with the recipient's first
 *   token of `displayName` at render time.
 * - `{courseId}` — the active course id.
 * - `{courseName}` — the course's brand name (`getCourseBrandName` in
 *   `@cip/core/courses`).
 * - `{courseWebOrigin}` — the origin the course is served from, with no
 *   trailing slash, so a link reads `{courseWebOrigin}/{courseId}/…`.
 *
 * The compose modal substitutes every token (`interpolateNudge` in the
 * webapp). The send callable substitutes `{courseName}` and
 * `{courseWebOrigin}` again, so a compose tab loaded before those tokens
 * existed cannot email them verbatim. Unknown tokens are left in-place and
 * logged at warn level (admin diagnostic).
 *
 * **Field meanings:**
 * - `enabled` — admin kill switch. Disabled nudges are not surfaced
 *   even when targeting matches.
 * - `eyebrow` — small uppercase label above the title in the card.
 *   Example: `"Optional · Pass it forward"`.
 * - `title` — bold title in the card. Supports the tokens above.
 *   Example: `"Suggest {recipientFirstName} apply to be an SL"`.
 * - `text` — italic suggested sentence, inserted into the note body when
 *   the sender taps "Add". Supports the tokens above.
 * - `appliesTo` — direction targeting. See {@link NudgeAppliesToSchema}.
 * - `priority` — lower number sorts first. Default is `100`.
 * - `courseId` — when present, only fires on that course; absent = global.
 * - `createdAt`, `updatedAt`, `createdBy` — audit fields.
 *
 * Stored Timestamps are validated as plain shapes (`{seconds, nanoseconds}`)
 * rather than the SDK's Timestamp class so this schema is hermetic from
 * both client and admin SDK imports.
 */
export const AppreciationNudgeSchema = z.object({
  enabled: z.boolean(),
  title: z.string().min(1).max(120),
  text: z.string().min(1).max(500),
  appliesTo: NudgeAppliesToSchema,
  priority: z.number().int().nonnegative(),
  courseId: z.string().min(1).optional(),
  createdAt: z.unknown(),
  updatedAt: z.unknown(),
  createdBy: z.string().min(1),
});

/** Parsed `appreciation_nudges` doc body. Doc id is the `nudgeId`. */
export type AppreciationNudge = z.infer<typeof AppreciationNudgeSchema>;

/** Default `priority` for new nudges. Lower = shown first. */
export const DEFAULT_NUDGE_PRIORITY = 100;
